import React, { Suspense, Fragment, lazy, useEffect } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import LoadingScreen from 'src/components/LoadingScreen';
import GuestGuard from 'src/components/GuestGuard';
import AuthGuard from 'src/components/AuthGuard';
import EditorLayout from 'src/views/editor/components/layout';
import WorkspaceLayout from './views/workspace/components/layouts/WorkspaceLayout';

type Routes = {
  exact?: boolean;
  path?: string | string[];
  guard?: any;
  layout?: any;
  component?: any;
  routes?: Routes;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={props => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes: Routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    exact: true,
    // guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/find-password',
    component: lazy(() => import('src/views/auth/FindPasswordView'))
  },
  {
    exact: true,
    // guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },

  {
    exact: true,
    guard: AuthGuard,
    path: '/workspace/draft',
    layout: WorkspaceLayout,
    component: lazy(() => import('src/views/workspace/Draft'))
  },
  {
    exact: true,
    // guard: GuestGuard,
    path: '/workspace/templates',
    layout: WorkspaceLayout,
    component: lazy(() => import('src/views/workspace/Templates'))
  },
  {
    exact: true,
    path: '/error',
    component: lazy(() => import('src/views/errors/NotSupportedView'))
  },
  {
    path: '/editor',
    guard: GuestGuard,
    routes: [
      {
        exact: true,
        path: '/editor/basic-scratch-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/choice-option-image/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/basic-bottom-sheet/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/card-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/floating-bottom-sheet/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-card-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/transparent-bottom-sheet/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/bottom-slide-up/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/choice-button-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/auto-square-slide/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/carousel-slide-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-icon-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/basic-full-countdown/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/bottom-sheet-image/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/radius-modal-slide/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/modal-slide/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/slide-modal-round/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/full-dual-image/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-survey/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-modal-slide/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/bottom-sheet-slide/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },

      {
        exact: true,
        path: '/editor/basic-full-image/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/radius-full-countdown/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/simple-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/multiple-choice-survey/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/modal-slider-survey/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/slide-full-image/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/slide-modal-image/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/auto-modal-slide/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/coupon-bottom-sheet/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/image-bottom-sheet/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },

      {
        exact: true,
        path: '/editor/alarm-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/basic-full-roulette/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },

      // 리스트 제외 템플릿
      {
        exact: true,
        path: '/editor/short-answer-survey/:id?',

        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/rating-answer-survey/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/nps-modal-survey/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        exact: true,
        path: '/editor/list-modal/:id?',
        layout: EditorLayout,
        component: lazy(() => import('src/views/editor/TemplatesView'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/billing',
    guard: AuthGuard,
    routes: [
      {
        exact: true,
        path: '/billing/subscription',
        layout: WorkspaceLayout,
        component: lazy(() => import('src/views/billing/Subscription'))
      },
      {
        exact: true,
        path: '/billing/completed',
        layout: WorkspaceLayout,
        component: lazy(() => import('src/views/billing/Completed'))
      },
      {
        exact: true,
        path: '/billing/payment',
        layout: WorkspaceLayout,
        component: lazy(() => import('src/views/billing/Payment'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },

  {
    path: '*',
    routes: [
      {
        exact: true,
        path: '/',
        component: lazy(() => import('src/views/main/MainView'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

export default routes;
